import { AmazonMarketplace } from '@conveyr/shared-types';
import { AxiosInstance } from 'axios';
import type { components, operations } from '@/api/generated/openapi';
import { axiosWithoutCaseMiddleware } from '@/helpers/forms/axios';
import listingsFactory from './listings';
import wfsFactory from './wfs';

interface ChannelsApiReturn {
    listings: ReturnType<typeof listingsFactory>
    wfs: ReturnType<typeof wfsFactory>
    fetchChannels: () => Promise<channelInterface[]>
    fetchChannel: (channelId: number) => Promise<channelInterface>
    syncListings: (channelId: number) => Promise<void>
    syncFnskus: (skus: string[]) => Promise<void>
    importListing: (channelId: number, identifier: string) => Promise<void>
    activateChannel: (channelId: number) => Promise<void>
    deactivateChannel: (channelId: number) => Promise<void>
    deleteChannel: (channelId: number) => Promise<void>
    getAmazonAuthorizeUrl: (channelId: number) => Promise<string>
    fetchAmazonMarketplaces: () => Promise<AmazonMarketplace[]>
    getWalmartAuthorizeUrl: (channelId: number) => Promise<string>
    createAmazonChannel: (form: CreateAmazonChannelRequest) => Promise<channelInterface>
    updateAmazonChannel: (channelId: number, form: UpdateAmazonChannelRequest) => Promise<void>
    createCustomChannel: (form: CreateCustomChannelRequest) => Promise<channelInterface>
    updateCustomChannel: (channelId: number, form: UpdateCustomChannelRequest) => Promise<void>
    createWalmartChannel: (form: CreateWalmartChannelRequest) => Promise<channelInterface>
    getAmazonChannelSubscriptions: (channelId: number) => Promise<AmazonChannelSubscription[]>
    createAmazonSubscription: (channelId: number, notificationType: AmazonChannelNotificationType) => Promise<void>
    deleteAmazonSubscription: (channelId: number, notificationType: AmazonChannelNotificationType) => Promise<void>
    searchAmazonForListings: (channelId: number, identifiers: string[], identifierType: string) => Promise<AmazonListingSearchResponse[] | null>
}

export type ChannelRaw = components['schemas']['Channel'];
export type ChannelType = ChannelRaw['type'];

// The server returns eBay and WooCommerce channels, but the UI has no connection flow for them.
export const creatableChannelTypes = ['amazon', 'walmart', 'custom'] as const satisfies readonly ChannelType[];

export const walmartMarketplaces = ['us', 'ca', 'mx'] as const;
export type WalmartMarketplace = typeof walmartMarketplaces[number];

export type FetchChannelsResponse = operations['list-channels']['responses']['200']['content']['application/json'];
export type FetchChannelResponse = operations['show-channel']['responses']['200']['content']['application/json'];
export type ActivateChannelResponse = operations['activate-channel']['responses']['200']['content']['application/json'];
export type DeactivateChannelResponse = operations['deactivate-channel']['responses']['200']['content']['application/json'];
export type DeleteChannelResponse = operations['delete-channel']['responses']['200']['content']['application/json'];
export type GetAmazonAuthorizeUrlResponse = operations['get-amazon-channel-authorize-url']['responses']['200']['content']['application/json'];
export type FetchAmazonMarketplacesResponse = operations['index-amazon-marketplaces']['responses']['200']['content']['application/json'];
export type GetWalmartAuthorizeUrlResponse = operations['get-walmart-channel-authorize-url']['responses']['200']['content']['application/json'];
export type CreateAmazonChannelResponse = operations['create-amazon-channel']['responses']['200']['content']['application/json'];
export type UpdateAmazonChannelResponse = operations['update-amazon-channel']['responses']['200']['content']['application/json'];
export type CreateCustomChannelResponse = operations['create-custom-channel']['responses']['200']['content']['application/json'];
export type UpdateCustomChannelResponse = operations['update-custom-channel']['responses']['200']['content']['application/json'];
export type CreateWalmartChannelResponse = operations['create-walmart-channel']['responses']['200']['content']['application/json'];

export type AmazonMarketplaceRaw = FetchAmazonMarketplacesResponse[number];

export type CreateAmazonChannelRequest = operations['create-amazon-channel']['requestBody']['content']['application/json'];
export type UpdateAmazonChannelRequest = operations['update-amazon-channel']['requestBody']['content']['application/json'];
export type CreateCustomChannelRequest = operations['create-custom-channel']['requestBody']['content']['application/json'];
export type UpdateCustomChannelRequest = operations['update-custom-channel']['requestBody']['content']['application/json'];
export type CreateWalmartChannelRequest = operations['create-walmart-channel']['requestBody']['content']['application/json'];

export interface channelInterface {
    id: number
    createdAt?: string
    updatedAt?: string
    merchantId: number
    type: ChannelType
    nickname: string | null
    channelAccountId: string | null | undefined
    connectionStatus: string
    countryId?: string
    marketplace?: WalmartMarketplace | null
}

function toChannel(raw: ChannelRaw): channelInterface {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        merchantId: raw.merchant_id,
        type: raw.type,
        nickname: raw.nickname,
        channelAccountId: raw.channel_account_id,
        connectionStatus: raw.connection_status,
        countryId: raw.country_id ?? undefined,
        marketplace: raw.marketplace,
    };
}

function toAmazonMarketplace(raw: AmazonMarketplaceRaw): AmazonMarketplace {
    return {
        identifier: raw.identifier,
        country: raw.country,
    };
}

interface AmazonChannelSubscriptionRaw {
    notification_type: AmazonChannelNotificationType
}

export interface AmazonChannelSubscription {
    notificationType: AmazonChannelNotificationType
}

function toAmazonChannelSubscription(r: AmazonChannelSubscriptionRaw): AmazonChannelSubscription {
    return {
        notificationType: r.notification_type,
    };
}

export enum AmazonChannelNotificationType {
    FeedProcessingFinished = 'FEED_PROCESSING_FINISHED',
    OrderChange = 'ORDER_CHANGE',
    ReportProcessingFinished = 'REPORT_PROCESSING_FINISHED',
    ListingsItemStatusChange = 'LISTINGS_ITEM_STATUS_CHANGE',
}

export interface importListingFormInterface {
    identifier: string
}

export interface AmazonListingSearchResponse {
    asin: string
    title: string
    condition: string | null
    sku: string
}

export default (axios: AxiosInstance = axiosWithoutCaseMiddleware): ChannelsApiReturn => ({
    listings: listingsFactory(axios),
    wfs: wfsFactory(axios),

    fetchChannels: async (): Promise<channelInterface[]> => {
        const response = await axios.get<FetchChannelsResponse>('/api/channels');

        return response.data.channels.map(toChannel);
    },

    fetchChannel: async (channelId: number): Promise<channelInterface> => {
        const response = await axios.get<FetchChannelResponse>(`/api/channels/${channelId}`);

        return toChannel(response.data.channel);
    },

    syncListings: async (channelId: number): Promise<void> => {
        return await axios.get(`/api/channels/${channelId}/sync`);
    },

    syncFnskus: async (skus: string[]): Promise<void> => {
        return await axios.post('/api/channels/sync-fnskus', { skus });
    },

    importListing: async (channelId: number, identifier: string): Promise<void> => {
        await axios.post(`/api/channels/${channelId}/import-listing`, { identifier });
    },

    activateChannel: async (channelId: number): Promise<void> => {
        await axios.get<ActivateChannelResponse>(`/api/channels/${channelId}/activate`);
    },

    deactivateChannel: async (channelId: number): Promise<void> => {
        await axios.get<DeactivateChannelResponse>(`/api/channels/${channelId}/deactivate`);
    },

    deleteChannel: async (channelId: number): Promise<void> => {
        await axios.delete<DeleteChannelResponse>(`/api/channels/${channelId}`);
    },

    getAmazonAuthorizeUrl: async (channelId: number): Promise<string> => {
        const response = await axios.get<GetAmazonAuthorizeUrlResponse>(`/api/channels/amazon/${channelId}/authorize-url`);

        return response.data.url;
    },

    fetchAmazonMarketplaces: async (): Promise<AmazonMarketplace[]> => {
        const response = await axios.get<FetchAmazonMarketplacesResponse>('/api/channels/amazon/marketplaces');

        return response.data.map(toAmazonMarketplace);
    },

    getWalmartAuthorizeUrl: async (channelId: number): Promise<string> => {
        const response = await axios.get<GetWalmartAuthorizeUrlResponse>(`/api/channels/walmart/${channelId}/authorize-url`);

        return response.data.url;
    },

    createAmazonChannel: async (form: CreateAmazonChannelRequest): Promise<channelInterface> => {
        const body: CreateAmazonChannelRequest = {
            marketplace: form.marketplace,
            nickname: form.nickname,
        };
        const response = await axios.post<CreateAmazonChannelResponse>('/api/channels/amazon', body);

        return toChannel(response.data.channel);
    },

    updateAmazonChannel: async (channelId: number, form: UpdateAmazonChannelRequest): Promise<void> => {
        const body: UpdateAmazonChannelRequest = {
            marketplace: form.marketplace,
            nickname: form.nickname,
        };
        await axios.put<UpdateAmazonChannelResponse>(`/api/channels/amazon/${channelId}`, body);
    },

    createCustomChannel: async (form: CreateCustomChannelRequest): Promise<channelInterface> => {
        const body: CreateCustomChannelRequest = { nickname: form.nickname };
        const response = await axios.post<CreateCustomChannelResponse>('/api/channels/custom', body);

        return toChannel(response.data.channel);
    },

    updateCustomChannel: async (channelId: number, form: UpdateCustomChannelRequest): Promise<void> => {
        const body: UpdateCustomChannelRequest = { nickname: form.nickname };
        await axios.put<UpdateCustomChannelResponse>(`/api/channels/custom/${channelId}`, body);
    },

    createWalmartChannel: async (form: CreateWalmartChannelRequest): Promise<channelInterface> => {
        const body: CreateWalmartChannelRequest = {
            marketplace: form.marketplace,
            nickname: form.nickname,
        };
        const response = await axios.post<CreateWalmartChannelResponse>('/api/channels/walmart', body);

        return toChannel(response.data.channel);
    },

    getAmazonChannelSubscriptions: async (channelId: number): Promise<AmazonChannelSubscription[]> => {
        const response = await axios.get<AmazonChannelSubscriptionRaw[]>(`/api/channels/${channelId}/subscription`);
        return response.data.map(toAmazonChannelSubscription);
    },

    createAmazonSubscription: async (channelId: number, notificationType: AmazonChannelNotificationType): Promise<void> => {
        await axios.post(`/api/channels/${channelId}/subscription`, {
            notification_type: notificationType,
        });
    },

    deleteAmazonSubscription: async (channelId: number, notificationType: AmazonChannelNotificationType): Promise<void> => {
        await axios.delete(`/api/channels/${channelId}/subscription`, {
            params: { notification_type: notificationType },
        });
    },

    searchAmazonForListings: async (
        channelId: number,
        identifiers: string[],
        identifierType: string,
    ): Promise<AmazonListingSearchResponse[] | null> => {
        const url = new URL(`/api/channels/amazon/${channelId}/search-for-listings`, location.origin);
        const skuTypes = new Set(['SKU', 'CUSTOM', 'FNSKU']);
        if (skuTypes.has(identifierType)) {
            identifiers.forEach(sku => url.searchParams.append('skus[]', sku));
        } else {
            identifiers.forEach(id => url.searchParams.append('identifiers[]', id));
            url.searchParams.set('identifiers_type', identifierType);
        }

        const response = await axios.get<{data: AmazonListingSearchResponse[]} | { message: string }>(
            url.toString(),
        );

        if ('data' in response.data) {
            return response.data.data;
        }

        return null;
    },
});
